import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Workflow, WorkflowListResponse } from "../../shared/types";
import { workflowApi } from "../services/api";

export const WorkflowListView: React.FC = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Workflow[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load sessions list
  const loadSessions = useCallback(async () => {
    setIsLoadingSessions(true);
    try {
      const result = await workflowApi.list(1, 50);
      if (result.success && result.data) {
        const data = result.data as WorkflowListResponse;
        const normalized: Workflow[] = (data.workflows || []).map((w: any) =>
          (w && (w.workflow || w)).id ? (w.workflow || w) : w,
        ) as Workflow[];
        setSessions(normalized);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load workflows");
      console.error("Failed to load sessions", e);
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleLoadSession = (workflowId: string) => {
    navigate(`/app/workflow/${workflowId}`);
  };

  const handleNewChat = async () => {
    try {
      const result = await workflowApi.create({
        name: "AI Agent Workflow",
        description: "Interactive chat session",
        objective: "New interactive chat session",
      });
      if (result.success && result.data) {
        const wf = result.data as Workflow;
        navigate(`/app/workflow/${wf.id}`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create new chat");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "running":
        return "text-green-500";
      case "completed":
        return "text-blue-500";
      case "failed":
        return "text-red-500";
      case "idle":
        return "text-gray-500";
      default:
        return "text-gray-400";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex-1 overflow-hidden">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 p-6 border-b border-border-primary">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Workflows</h1>
              <p className="text-gray-400 mt-1">Manage your AI agent workflows</p>
            </div>
            <button
              onClick={handleNewChat}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Workflow
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200">
              {error}
            </div>
          )}

          {isLoadingSessions ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">No workflows yet</h3>
              <p className="text-gray-400 mb-6">Create your first AI agent workflow to get started</p>
              <button
                onClick={handleNewChat}
                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                Create Workflow
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sessions.map((workflow) => (
                <div
                  key={workflow.id}
                  onClick={() => handleLoadSession(workflow.id)}
                  className="bg-bg-card border border-border-primary rounded-lg p-6 hover:border-primary-500/50 transition-colors cursor-pointer group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white group-hover:text-primary-400 transition-colors">
                      {workflow.name}
                    </h3>
                    <div
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(workflow.status || "idle")}`}
                    >
                      {(workflow.status || "idle").toUpperCase()}
                    </div>
                  </div>
                  
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {workflow.objective}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>{formatDate(workflow.createdAt)}</span>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
